import React from 'react';
import { FiPlus } from 'react-icons/fi';
import { ProjectTile } from '../domain/types';
import { techLabel } from '../domain/techLabel';
import { Status } from '../../common/domain/types';

/** Compact analysis-window label like "Nov 2019 – Dec 2025". */
function formatAnalysisPeriod(startIso?: string | null, endIso?: string | null): string | null {
    if (!startIso || !endIso) return null;
    const toLabel = (iso: string) => {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso.slice(0, 7);
        return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    };
    return `${toLabel(startIso)} – ${toLabel(endIso)}`;
}

interface ProjectGridProps {
    projects: ProjectTile[];
    onProjectClick: (project: ProjectTile) => void;
    onCreateClick: () => void;
    isLoading?: boolean;
    error?: Error | null;
}

export function ProjectGrid({
    projects,
    onProjectClick,
    onCreateClick,
    isLoading,
    error
}: ProjectGridProps) {
    if (isLoading) {
        return <p className="text-sm text-text-muted">Loading…</p>;
    }

    if (error) {
        return <p className="text-sm text-health-400">{error.message}</p>;
    }

    return (
        <div className="grid gap-4 sm:grid-cols-2">
            {projects.map((p) => (
                <div
                    key={p.project_id}
                    onClick={() => onProjectClick(p)}
                    className="flex min-h-[6rem] p-4 cursor-pointer flex-col items-center justify-center rounded-lg bg-surface-600 hover:bg-surface-500 border border-border transition-colors"
                >
                    <span className="text-center font-medium break-words max-w-full text-text-primary">{p.name}</span>
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-1">
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.status === "Operating"
                                    ? "bg-quantum-500/20 text-quantum-400"
                                    : "bg-economic-500/20 text-economic-400"
                                }`}
                        >
                            {p.status}
                        </span>
                        {p.tech_type && (
                            <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-surface-500 text-text-secondary">
                                {techLabel(p.tech_type)}
                            </span>
                        )}
                    </div>
                    {(() => {
                        const period = formatAnalysisPeriod(p.latest_run_start_date, p.latest_run_end_date);
                        const ba = p.eia_ba_code;
                        const cap = typeof p.mw_capacity === "number" ? `${p.mw_capacity.toFixed(p.mw_capacity < 10 ? 2 : 1)} MW` : null;
                        const bits = [cap, ba, period].filter(Boolean);
                        if (bits.length === 0) return null;
                        return (
                            <div className="mt-2 text-center text-xs text-text-muted">
                                {bits.join(" · ")}
                            </div>
                        );
                    })()}
                </div>
            ))}

            <div
                onClick={onCreateClick}
                className="flex min-h-[6rem] p-4 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-surface-600 text-text-muted hover:bg-surface-500 hover:text-quantum-400 transition-colors"
            >
                <FiPlus className="text-2xl" />
                <span className="mt-1 text-sm font-medium">Create New Project</span>
            </div>
        </div>
    );
}